import { BeakerIcon, DocumentDuplicateIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Badge, Button, Heading, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import type { GetPromptDetailsOutput, PromptRegistryInput, ReportTypeEnum } from "@api/rest/types-and-hooks";
import {
  RegistryStatusEnum,
  getGetPromptDetailsQueryKey,
  useBasecampOrganizationsRetrieve,
  useDeleteDraftPrompt,
  useGetPromptDetails,
  useGetPromptDraft,
  useListAvailableInputs,
  useUpdatePrompt,
} from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { DuplicatePromptDialog } from "@components/Prompts/DuplicatePromptDialog";
import { OrgOwnerSelect } from "@components/Prompts/OrgOwnerSelect";
import { ReportTypeCheckboxes, ReportTypeChips } from "@components/Prompts/ReportTypeTags";
import { GLOBAL_ORG_SENTINEL } from "@components/Prompts/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

/** Yaml-editable fields we render on this page. Matches
 *  ``_DRAFT_YAML_FIELDS`` on the api-django side (the one the "Open PR"
 *  flow ships to yaml). Any field the draft carries but the registry
 *  hasn't merged yet gets rendered here on the "draft only" fallback. */
interface DraftOverrides {
  role?: string | null;
  templated_instructions?: string | null;
  instructions?: string | null;
  technical_instructions?: string | null;
  sample_output?: string | null;
  response_schema?: unknown;
  inputs?: { key: string }[];
}

const SOURCE_LABELS: Record<string, string> = {
  assessment: "Assessment",
  parsed_file: "Parsed File",
  report_section: "Report Section",
  biographic: "Biographic",
  interview: "Interview",
  clinician: "Clinician",
  goal_data: "Goal Data",
  computed: "Computed",
};

// Stable display order for input source groups. Anything not in this list
// is appended in alphabetical order at the end.
const SOURCE_ORDER: string[] = [
  "assessment",
  "parsed_file",
  "report_section",
  "biographic",
  "interview",
  "clinician",
  "goal_data",
  "computed",
];

function sortedSourceEntries(grouped: Record<string, PromptRegistryInput[]>): [string, PromptRegistryInput[]][] {
  const orderedKeys = SOURCE_ORDER.filter((s) => grouped[s]);
  const extraKeys = Object.keys(grouped)
    .filter((s) => !SOURCE_ORDER.includes(s))
    .sort();
  return [...orderedKeys, ...extraKeys].map((k) => [k, grouped[k]]);
}

function InputCard({ input }: { input: PromptRegistryInput }) {
  const facets: { label: string; value: string }[] = [];

  if (input.file_kind) facets.push({ label: "File Kind", value: input.file_kind });
  if (input.section_identifier) facets.push({ label: "Section", value: input.section_identifier });
  if (input.nested_keys?.length) facets.push({ label: "Keys", value: input.nested_keys.join(", ") });
  if (input.produced_by) facets.push({ label: "Produced By", value: input.produced_by });
  if (input.is_enriched != null) facets.push({ label: "Enriched", value: input.is_enriched ? "Yes" : "No" });
  if (input.is_curriculum_based != null)
    facets.push({ label: "Curriculum-Based", value: input.is_curriculum_based ? "Yes" : "No" });

  return (
    <div className="flex flex-col gap-1 p-4 rounded-lg border border-border-medium bg-bg-default">
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono text-text-primary">{input.key}</code>
        {input.value_schema && (
          <span className="text-xs px-2 py-0.5 rounded bg-limestone-100 text-text-secondary font-mono">
            {input.value_schema}
          </span>
        )}
      </div>
      {input.description && (
        <Paragraph size="small" colorType="secondary">
          {input.description}
        </Paragraph>
      )}
      {facets.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
          {facets.map((f) => (
            <div key={f.label} className="flex gap-1 items-baseline">
              <Label size="small" colorType="secondary">
                {f.label}:
              </Label>
              <Paragraph size="small" colorType="primary">
                {f.value}
              </Paragraph>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A draft's ``inputs`` only ever carries bare ``{key}`` entries (see
 * ``_ALLOWED_DRAFT_FIELDS`` on the api-django side) — no source/
 * description/file_kind, since that metadata lives in the shared input
 * catalog, not the draft itself. Mirrors the backend's own resolution
 * for matched prompts (``_resolve_prompt_details_response``): look up
 * each key in the catalog, falling back to its parent for a dotted
 * leaf (``biographic_info.name`` → ``biographic_info``), and dedupe by
 * the resolved entry so multiple leaves of the same parent collapse
 * into one card — otherwise a draft's inputs render as a flat,
 * unlabeled "custom" list instead of matching how the same prompt
 * looks once it's shipped to yaml.
 */
function resolveDraftInputs(
  rawInputs: { key: string }[],
  catalog: Map<string, PromptRegistryInput>,
): PromptRegistryInput[] {
  const resolved: PromptRegistryInput[] = [];
  const seen = new Set<string>();
  for (const { key } of rawInputs) {
    const entry = catalog.get(key) ?? catalog.get(key.split(".")[0]);
    if (entry) {
      if (!seen.has(entry.key)) {
        resolved.push(entry);
        seen.add(entry.key);
      }
    } else {
      resolved.push({ key, source: "custom" } as PromptRegistryInput);
    }
  }
  return resolved;
}

function InputsSection({ inputs }: { inputs: PromptRegistryInput[] }) {
  const grouped = inputs.reduce<Record<string, PromptRegistryInput[]>>((acc, input) => {
    const group = input.source;
    if (!acc[group]) acc[group] = [];
    acc[group].push(input);
    return acc;
  }, {});

  if (inputs.length === 0) {
    return (
      <Paragraph colorType="secondary" className="italic">
        No inputs defined for this prompt.
      </Paragraph>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {sortedSourceEntries(grouped).map(([source, items]) => (
        <div key={source} className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <Label size="small" colorType="secondary" className="uppercase tracking-wide">
              {SOURCE_LABELS[source] ?? source}
            </Label>
            <span className="text-xs text-text-tertiary">({items.length})</span>
          </div>
          <div className="flex flex-col gap-2">
            {items.map((input) => (
              <InputCard key={input.key} input={input} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContentField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <Label size="small" colorType="secondary">
        {label}
      </Label>
      <div className="prose prose-sm max-w-none text-text-primary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <ReactMarkdown>{value}</ReactMarkdown>
      </div>
    </div>
  );
}

function PromptContentSection({ prompt }: { prompt: GetPromptDetailsOutput }) {
  // Raw-source toggling used to live here; it's been removed now that the
  // test bed (`/prompts/:key/test-bed`) is the canonical place to look
  // at / edit the underlying source. This section renders markdown-preview
  // only.
  const hasContent =
    prompt.role ||
    prompt.instructions ||
    prompt.templated_instructions ||
    prompt.sample_output ||
    prompt.technical_instructions ||
    prompt.response_schema != null;

  if (!hasContent) {
    return (
      <Paragraph colorType="secondary" className="italic">
        No prompt content available.
      </Paragraph>
    );
  }

  return (
    <div className="flex flex-col gap-6 overflow-y-auto max-h-[65vh] pr-1">
      <ContentField label="Role" value={prompt.role} />
      <ContentField label="Templated Instructions" value={prompt.templated_instructions} />
      <ContentField label="Instructions" value={prompt.instructions} />
      <ContentField label="Technical Instructions" value={prompt.technical_instructions} />
      <ContentField label="Sample Output" value={prompt.sample_output} />
      {prompt.response_schema != null && (
        <div className="flex flex-col gap-1">
          <Label size="small" colorType="secondary">
            Response Schema
          </Label>
          <pre className="text-sm bg-limestone-50 rounded-lg p-4 overflow-auto text-text-primary border border-border-medium">
            {JSON.stringify(prompt.response_schema, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export const PromptDetail: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  // Inline edit of the DB-owned metadata (name, organization owner, report types).
  // Content fields stay with the Evaluations draft flow — this replaces
  // the old Update Prompt dialog on the list page.
  const [editingDetails, setEditingDetails] = useState(false);
  const [editName, setEditName] = useState("");
  const [editOrgId, setEditOrgId] = useState<string>(GLOBAL_ORG_SENTINEL);
  const [editReportTypes, setEditReportTypes] = useState<ReportTypeEnum[]>([]);
  const queryClient = useQueryClient();
  const updateMutation = useUpdatePrompt();
  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({}, { query: { enabled: editingDetails } });
  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );

  const { data, isLoading, isError, error } = useGetPromptDetails(key ?? "", {
    query: { enabled: !!key },
  });

  const prompt = data?.status === 200 ? data.data : undefined;
  // Cover both shapes of failure:
  //   - the request reached the server but returned non-200 (data.status set)
  //   - the request never produced a parseable response (fetch/JSON.parse threw)
  //     → React Query sets isError with data undefined
  let errorDetail: string | undefined;
  if (data && data.status !== 200) {
    errorDetail = (data.data as { detail?: string } | undefined)?.detail ?? `Request failed (${data.status})`;
  } else if (isError) {
    errorDetail = error instanceof Error ? error.message : "Failed to load prompt.";
  }

  // Also fetch the draft — when the registry has no yaml entry, a
  // populated draft is the substance the Evaluations page renders. If
  // this page ignores the draft, users see "No YAML implementation"
  // here but the same prompt looks fully populated on Evaluations,
  // which reads as a bug. Draft fetch runs unconditionally so it's
  // ready if we need to fall back; only used on the missing-yaml path.
  const { data: draftResp, isLoading: isDraftLoading } = useGetPromptDraft(key ?? "", {
    query: { enabled: !!key },
  });
  const draftOverrides: DraftOverrides | null =
    draftResp?.status === 200 ? ((draftResp.data.draft_overrides ?? null) as DraftOverrides | null) : null;

  // Shared input catalog, used to enrich a draft-only prompt's bare
  // {key} entries with the same metadata a shipped prompt gets from
  // the yaml registry. See resolveDraftInputs above.
  const { data: allInputsResp } = useListAvailableInputs();
  const allInputsByKey = useMemo(() => {
    if (allInputsResp?.status !== 200) return new Map<string, PromptRegistryInput>();
    return new Map((allInputsResp.data.inputs as PromptRegistryInput[]).map((i) => [i.key, i]));
  }, [allInputsResp]);
  const hasDraftContent = Boolean(
    draftOverrides &&
    (draftOverrides.role ||
      draftOverrides.instructions ||
      draftOverrides.templated_instructions ||
      draftOverrides.technical_instructions ||
      draftOverrides.sample_output ||
      draftOverrides.response_schema != null ||
      (draftOverrides.inputs && draftOverrides.inputs.length > 0)),
  );

  // When the prompt row has been deleted, redirect to the list. The
  // delay lets Rio's reply poll into the drawer first so the user sees
  // "I've deleted it" before the redirect.
  const navigate = useNavigate();
  const promptWasDeleted = data?.status === 400;
  useEffect(() => {
    if (!promptWasDeleted) return;
    const t = window.setTimeout(() => navigate("/prompts", { replace: true }), 3000);
    return () => window.clearTimeout(t);
  }, [promptWasDeleted, navigate]);

  const deleteMutation = useDeleteDraftPrompt({
    mutation: {
      onSuccess: (resp) => {
        if (resp.status === 200) {
          notifySuccess(`Deleted draft prompt '${key}'.`);
          navigate("/prompts", { replace: true });
        } else {
          const detail = (resp.data as { detail?: string } | undefined)?.detail;
          notifyError(detail ?? `Couldn't delete prompt (status ${resp.status}).`);
          setConfirmingDelete(false);
        }
      },
      onError: (err) => {
        notifyError(err instanceof Error ? err.message : "Couldn't delete prompt.");
        setConfirmingDelete(false);
      },
    },
  });

  const startEditingDetails = () => {
    if (!prompt) return;
    setEditName(prompt.name);
    setEditOrgId(prompt.organization?.id != null ? String(prompt.organization.id) : GLOBAL_ORG_SENTINEL);
    setEditReportTypes(prompt.report_types);
    setEditingDetails(true);
  };

  const saveDetails = async () => {
    if (!prompt || !editName.trim()) return;
    try {
      const resp = await updateMutation.mutateAsync({
        data: {
          id: prompt.id,
          name: editName.trim(),
          organization_id: editOrgId === GLOBAL_ORG_SENTINEL ? null : editOrgId,
          // Omit when empty: the API rejects [] and untagged legacy prompts stay as-is.
          ...(editReportTypes.length ? { report_types: editReportTypes } : {}),
        },
      });
      if (resp.status === 200) {
        notifySuccess("Prompt updated.");
        setEditingDetails(false);
        await queryClient.invalidateQueries({ queryKey: getGetPromptDetailsQueryKey(key ?? "") });
      } else {
        notifyError((resp.data as { detail?: string })?.detail ?? "Failed to update prompt.");
      }
    } catch {
      notifyError("Failed to update prompt — check your connection.");
    }
  };

  // Wait on both details AND draft before rendering. Without the draft
  // gate, registry-missing prompts flashed the "No YAML implementation"
  // empty state before the draft response landed and the page could
  // switch to the draft fallback.
  if (isLoading || isDraftLoading) return null;

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
      <Breadcrumb crumbs={[{ label: "Prompts", to: "/prompts" }, { label: prompt?.name ?? key ?? "..." }]} />

      {/* Header */}
      <div className="flex flex-row items-center gap-3 mb-1 flex-wrap">
        {editingDetails ? (
          <input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            className="min-w-72 rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-xl font-semibold text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-900"
            placeholder="Prompt name"
            autoFocus
          />
        ) : (
          <Heading size="medium">{prompt?.name ?? key}</Heading>
        )}
        {prompt && prompt.registry_status === RegistryStatusEnum.matched && (
          <Badge text="Matched" appearance="success" />
        )}
        {prompt && prompt.registry_status !== RegistryStatusEnum.matched && hasDraftContent && (
          <Badge text="Draft only — not yet in YAML" appearance="warn" />
        )}
        {prompt && prompt.registry_status !== RegistryStatusEnum.matched && !hasDraftContent && (
          <Badge text="No YAML implementation" appearance="warn" />
        )}
        {key && (
          <div className="ml-auto flex items-center gap-2">
            {prompt && prompt.registry_status !== RegistryStatusEnum.matched && (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border-medium bg-bg-surface text-sm text-red-700 transition-colors hover:cursor-pointer hover:bg-red-50 hover:border-red-300"
                title="Only allowed while this prompt is draft-only (not yet in yaml)."
              >
                <TrashIcon className="size-4" />
                Delete prompt
              </button>
            )}
            {prompt && !editingDetails && (
              <button
                type="button"
                onClick={startEditingDetails}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border-medium bg-bg-surface text-sm text-text-primary transition-colors hover:cursor-pointer hover:bg-bg-default"
              >
                <PencilSquareIcon className="size-4" />
                Edit details
              </button>
            )}
            {prompt && editingDetails && (
              <>
                <button
                  type="button"
                  onClick={() => void saveDetails()}
                  disabled={
                    updateMutation.isPending ||
                    !editName.trim() ||
                    // Tagged prompts can't be cleared back to untagged.
                    (prompt.report_types.length > 0 && editReportTypes.length === 0)
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-limestone-900 bg-limestone-900 text-sm text-white transition-colors hover:cursor-pointer disabled:opacity-40"
                >
                  {updateMutation.isPending ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingDetails(false)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border-medium bg-bg-surface text-sm text-text-secondary transition-colors hover:cursor-pointer hover:bg-bg-default"
                >
                  Cancel
                </button>
              </>
            )}
            {prompt && (
              <button
                type="button"
                onClick={() => setDuplicateDialogOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border-medium bg-bg-surface text-sm text-text-primary transition-colors hover:cursor-pointer hover:bg-bg-default"
              >
                <DocumentDuplicateIcon className="size-4" />
                Duplicate Prompt
              </button>
            )}
            <Link
              to={`/prompts/${encodeURIComponent(key)}/evaluations`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-border-medium bg-bg-surface bg-rio-100 text-sm text-text-primary hover:bg-bg-default transition-colors"
            >
              <BeakerIcon className="size-4" />
              Evaluations
            </Link>
          </div>
        )}
      </div>

      {prompt && (
        <DuplicatePromptDialog
          isDialogOpen={duplicateDialogOpen}
          setDialogOpen={setDuplicateDialogOpen}
          sourceKey={prompt.key}
          sourceName={prompt.name}
          sourceOrganizationId={prompt.organization?.id}
          sourceReportTypes={prompt.report_types}
        />
      )}

      {confirmingDelete && key && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-bg-default p-6 shadow-lg">
            <Heading size="small">Delete this prompt?</Heading>
            <Paragraph colorType="secondary" className="mt-2">
              <code className="text-sm font-mono text-text-primary">{key}</code> only exists as a database row — it
              hasn&apos;t been committed to yaml yet. Deleting is not undoable.
            </Paragraph>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                text="Cancel"
                variant="secondary"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleteMutation.isPending}
              />
              <Button
                text={deleteMutation.isPending ? "Deleting…" : "Delete prompt"}
                variant="negative"
                onClick={() => deleteMutation.mutate({ key })}
                disabled={deleteMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-4 mb-6">
        <code className="text-sm font-mono text-text-secondary">{prompt?.key ?? key}</code>
        {editingDetails ? (
          <>
            <div className="w-72">
              <OrgOwnerSelect orgs={orgs} value={editOrgId} onChange={setEditOrgId} label="Organization owner" />
            </div>
            <ReportTypeCheckboxes value={editReportTypes} onChange={setEditReportTypes} />
          </>
        ) : (
          <>
            {prompt?.organization && (
              <Paragraph size="small" colorType="secondary">
                {prompt.organization.name}
              </Paragraph>
            )}
            {prompt && <ReportTypeChips reportTypes={prompt.report_types} />}
          </>
        )}
      </div>

      {errorDetail ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-border-medium bg-bg-surface text-center">
          <Heading size="small">Couldn&apos;t load prompt</Heading>
          <Paragraph colorType="secondary" className="max-w-lg">
            {errorDetail}
          </Paragraph>
        </div>
      ) : prompt?.registry_status === RegistryStatusEnum.missing && !hasDraftContent ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-border-medium bg-bg-surface text-center">
          <Heading size="small">No YAML implementation</Heading>
          <Paragraph colorType="secondary" className="max-w-lg">
            This prompt is registered in the database but has no implementation in the YAML registry. It cannot be
            invoked by workers until it is added to the pipelines repo.
          </Paragraph>
        </div>
      ) : (
        prompt && (
          <div className="flex flex-col gap-4">
            {/* Draft-only fallback: the yaml registry has no entry for
                this key, but a draft carries the current substance
                (typical after "New Prompt" + before the "Open PR" flow's
                PR has merged upstream). The "Draft only — not yet in
                YAML" badge next to the title already communicates the
                state; no banner needed. */}
            <div className="grid grid-cols-2 gap-8 items-start">
              {/* Inputs */}
              <div className="flex flex-col gap-4 p-8 bg-bg-surface border border-border-medium rounded-xl">
                <Heading size="small">Inputs</Heading>
                <InputsSection
                  inputs={
                    prompt.registry_status === RegistryStatusEnum.matched
                      ? prompt.inputs
                      : resolveDraftInputs(draftOverrides?.inputs ?? [], allInputsByKey)
                  }
                />
              </div>

              {/* Prompt Content */}
              <div className="flex flex-col gap-4 p-8 bg-bg-surface border border-border-medium rounded-xl">
                <Heading size="small">Prompt Content</Heading>
                <PromptContentSection
                  prompt={
                    prompt.registry_status === RegistryStatusEnum.matched
                      ? prompt
                      : ({
                          ...prompt,
                          role: draftOverrides?.role ?? null,
                          templated_instructions: draftOverrides?.templated_instructions ?? null,
                          instructions: draftOverrides?.instructions ?? null,
                          technical_instructions: draftOverrides?.technical_instructions ?? null,
                          sample_output: draftOverrides?.sample_output ?? null,
                          // response_schema is DB-backed (PROC-103) and the
                          // details API serves it even when the registry has
                          // no yaml entry — fall back to it when the draft has
                          // no schema entry at all. Key-presence check (not ??):
                          // an explicit null in the draft means "schema cleared"
                          // and must not resurrect the DB value.
                          response_schema:
                            draftOverrides && "response_schema" in draftOverrides
                              ? draftOverrides.response_schema
                              : prompt.response_schema,
                        } as GetPromptDetailsOutput)
                  }
                />
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};
